// Reverse cross-link: embassy/$country (+ embassy/$country/$service) →
// marriage/$nationality. Closes the loop with MarriageEmbassyLinks so the
// marriage ↔ embassy cluster has bidirectional forward edges, which helps
// GoogleBot consolidate topical authority across both pillars.

import { Link } from "@tanstack/react-router";
import { Heart, ArrowRight, HeartHandshake } from "lucide-react";
import { MARRIAGE_NATIONALITIES, getMarriageNationality } from "@/data/marriage-nationalities";

// Embassy country slug → marriage nationality slug (inverse of
// NATIONALITY_TO_EMBASSY in MarriageEmbassyLinks). Keep in sync.
const EMBASSY_TO_NATIONALITY: Record<string, string> = {
  usa: "american",
  canada: "canadian",
  uk: "british",
  germany: "german",
  france: "french",
  italy: "italian",
  spain: "spanish",
  netherlands: "dutch",
  belgium: "belgian",
  switzerland: "swiss",
  sweden: "swedish",
  norway: "norwegian",
  denmark: "danish",
  finland: "finnish",
  austria: "austrian",
  portugal: "portuguese",
  ireland: "irish",
  japan: "japanese",
  korea: "korean",
  china: "chinese",
  india: "indian",
  indonesia: "indonesian",
  malaysia: "malaysian",
  philippines: "filipino",
  singapore: "singaporean",
  vietnam: "vietnamese",
  australia: "australian",
  "new-zealand": "new-zealander",
  uae: "emirati",
};

type Props = {
  country: string;    // embassy country slug
  countryTh: string;  // e.g. "สหรัฐอเมริกา"
};

export function EmbassyMarriageLinks({ country, countryTh }: Props) {
  const natSlug = EMBASSY_TO_NATIONALITY[country];
  if (!natSlug) return null;
  const n = getMarriageNationality(natSlug);
  if (!n) return null;

  // Related: 3 other nationalities in the same region (excluding current)
  const others = MARRIAGE_NATIONALITIES
    .filter((x) => x.slug !== n.slug && x.region === n.region)
    .slice(0, 3);

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-rose-600">
        จดทะเบียนสมรส · Marriage Registration
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        จดทะเบียนสมรสกับ{n.nameTh} {n.flag}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        นอกจากรับรองเอกสารสถานทูต{countryTh} เราพาคู่หมั้น{n.nameTh}ไป Affidavit
        ที่สถานทูต + แปลไทย + รับรอง MFA + นัดอำเภอ (บางรัก/วัฒนา/ปทุมวัน)
        ครบจบในแพ็กเกจเดียว เริ่ม ฿{n.serviceFeeTh.toLocaleString()} เสร็จ {n.turnaround}.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/marriage/$nationality"
          params={{ nationality: n.slug }}
          className="group flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50/40 p-4 transition hover:-translate-y-0.5 hover:border-rose-400 hover:shadow-md"
          data-track-zone="embassy-marriage-hub"
        >
          <HeartHandshake className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="flex-1">
            <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-rose-700">
              จดทะเบียนสมรสกับ{n.nameTh}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-600">
              เริ่ม ฿{n.serviceFeeTh.toLocaleString()} · {n.turnaround}
            </div>
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 group-hover:gap-1.5">
              ดูขั้นตอน <ArrowRight className="h-3 w-3" />
            </div>
          </div>
        </Link>

        {others.map((o) => (
          <Link
            key={o.slug}
            to="/marriage/$nationality"
            params={{ nationality: o.slug }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="embassy-marriage-nearby"
          >
            <Heart className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />
            <div className="flex-1">
              <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">
                {o.flag} {o.nameTh}
              </div>
              <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-600">
                เริ่ม ฿{o.serviceFeeTh.toLocaleString()} · {o.turnaround}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 group-hover:gap-1.5">
                อ่านขั้นตอน <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
